<template>
  <div>
    <LISAMenu
      v-model="menuVisible"
      class="EventButtonList"
      :items="menuItems"
      placement="right-start"
    >
      <template #trigger="{ triggerAttrs }">
        <LISAUIButton
          v-bind="triggerAttrs"
          icon="fas fa-bars"
          size="sm"
          text
          :variant="archivingBlocked ? 'primary' : 'success'"
          @click.stop
        />
      </template>
    </LISAMenu>

    <MoveEventModal v-if="showMoveEventModal" v-model="showMoveEventModal" :event="event" />

    <SQDCEventLinkageModal
      v-if="eventLinkageModalVisibility"
      v-model:visible="eventLinkageModalVisibility"
      :event="event"
      :event-types="eventTypes"
    />
  </div>
</template>

<script setup lang="ts">
import { type PropType, ref, computed } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import {
  LISAButton as LISAUIButton,
  LISAMenu,
  type LISAMenuGroup,
  type LISAMenuItem,
} from "@fi-solutions/lisa-ui";
import { useStore } from "vuex";
import {
  useSQDCEventArchiveMutation,
  SQDCEventRevisionFieldType,
  SQDCActionStatus,
} from "@/graphql";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";
import MoveEventModal from "../MoveEventModal.vue";
import SQDCEventLinkageModal from "../SQDCEventLinkageModal.vue";
import type { EABEventType, EABEventTypeType } from "../EventActionHelper";

const props = defineProps({
  event: {
    type: Object as PropType<EABEventType>,
    required: true,
  },
  eventTypes: {
    type: Array as PropType<EABEventTypeType[]>,
    required: false,
    default: () => [],
  },
});
const store = useStore();

const showMoveEventModal = ref(false);

const menuVisible = ref(false);

const { t } = useTranslation();

const { archiveWithoutTasks } = useSQDCGlobalSettings();

const actions = computed(() =>
  props.event.ActionEventConnection.map((connection) => connection.action).filter(
    (action) => action != null,
  ),
);

const hasOpenActions = computed(() =>
  actions.value.some((action) => action.status !== SQDCActionStatus.ACT),
);

const archivingBlocked = computed(
  () => hasOpenActions.value || (!archiveWithoutTasks.value && actions.value.length === 0),
);

const archivingBlockedReason = computed(() => {
  if (actions.value.length === 0 && !archiveWithoutTasks.value) {
    return t("components.issueModal.archivingWithoutTasksNotAllowed");
  }
  if (hasOpenActions.value) {
    return t("components.eventActionBoard.IssueConfigButtonList.taskAndIHOrderOpen");
  }
  return t("components.eventActionBoard.IssueConfigButtonList.archiveBlocked");
});

const { mutate: archiveEvent } = useSQDCEventArchiveMutation();

const eventLinkageModalVisibility = ref(false);

function openEventLinkage() {
  eventLinkageModalVisibility.value = true;
}

type ConfigMenuEntry = Omit<LISAMenuItem, "id" | "callback" | "tooltip"> & {
  key?: string;
  command: () => void;
};

const menuItems = computed<LISAMenuGroup[]>(() => {
  let entries = <ConfigMenuEntry[]>[];

  if (props.event.deletedAt) {
    entries.push({
      label: t("components.eventActionBoard.IssueConfigButtonList.reactivateIssue"),
      icon: "fas fa-trash-restore-alt text-primary",
      command: () =>
        archiveEvent(
          {
            eventId: props.event.id,
            archive: false,
          },
          {
            optimisticResponse: {
              SQDCEventArchive: {
                __typename: "SQDCEvent",
                id: props.event.id,
                // Apollo requires an explicit null for selected nullable fields; undefined means omitted.
                deletedAt: null as never,
                updatedAt: DateTime.now().toISO(),
                SQDCEventRevisions: [
                  {
                    __typename: "SQDCEventRevision",
                    id: -1,
                    changes: [
                      {
                        __typename: "SQDCEventRevisionChange",
                        fieldType: SQDCEventRevisionFieldType.ARCHIVE,
                        fieldId: null as any,
                        oldValue: true,
                        newValue: false,
                        includeInLast: true,
                      },
                    ],
                    changedById: store.getters["login/userId"],
                    changedBy: {
                      __typename: "User",
                      id: store.getters["login/userId"],
                      preferredName: store.getters["login/preferredName"],
                    },
                    createdAt: DateTime.now().toISO(),
                    comment: null as any,
                  },
                  ...props.event.SQDCEventRevisions.slice(-4),
                ],
              },
            },
          },
        ),
    });
  } else {
    entries = [
      {
        label: t("components.eventActionBoard.IssueConfigButtonList.escalateDelegate"),
        icon: "pi pi-directions text-primary",
        command: () => {
          showMoveEventModal.value = true;
        },
      },
      {
        disabled: archivingBlocked.value,
        variant: archivingBlocked.value ? "secondary" : "success",
        label: t("components.eventActionBoard.IssueConfigButtonList.archive"),
        icon: "fas fa-archive",
        key: archivingBlocked.value ? archivingBlockedReason.value : undefined,
        command: () => {
          archiveEvent(
            {
              eventId: props.event.id,
              archive: true,
            },
            {
              optimisticResponse: {
                SQDCEventArchive: {
                  __typename: "SQDCEvent",
                  id: props.event.id,
                  deletedAt: DateTime.now().toISO(),
                  updatedAt: DateTime.now().toISO(),
                  SQDCEventRevisions: [
                    {
                      __typename: "SQDCEventRevision",
                      id: -1,
                      changes: [
                        {
                          __typename: "SQDCEventRevisionChange",
                          fieldType: SQDCEventRevisionFieldType.ARCHIVE,
                          fieldId: null as any,
                          oldValue: false,
                          newValue: true,
                          includeInLast: true,
                        },
                      ],
                      changedById: store.getters["login/userId"],
                      changedBy: {
                        __typename: "User",
                        id: store.getters["login/userId"],
                        preferredName: store.getters["login/preferredName"],
                      },
                      createdAt: DateTime.now().toISO(),
                      comment: null as any,
                    },
                    ...props.event.SQDCEventRevisions.slice(-4),
                  ],
                },
              },
            },
          );
        },
      },
      {
        label: t("components.eventActionBoard.IssueConfigButtonList.eventLinkage"),
        icon: "fas fa-link text-primary",
        command: () => {
          openEventLinkage();
        },
      },
    ];
  }
  return [
    {
      label: t("global.edit"),
      items: entries.map((entry, index) => ({
        ...entry,
        id: index,
        tooltip: entry.key,
        callback: entry.command,
      })),
    },
  ];
});
</script>
